import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  AppWindow,
  Building2,
  ChevronDown,
  ChevronRight,
  Layers,
  Server,
  UserRound,
} from 'lucide-react';

import { toneClasses, toneFromStatus, StatusTone } from './status';

export type DiagramNodeKind = 'user' | 'org' | 'client' | 'forge' | 'app';

export interface DiagramNodeData {
  kind: DiagramNodeKind;
  label: string;
  sublabel?: string | null;
  status?: string | null;
  collapsible?: boolean;
  collapsed?: boolean;
  hiddenSummary?: string | null;
  onToggleCollapse?: (nodeId: string) => void;
}

const NODE_WIDTH = 220;
const NODE_MIN_HEIGHT = 58;

const hiddenHandleStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  width: 1,
  height: 1,
  minWidth: 1,
  minHeight: 1,
  pointerEvents: 'none',
};

const NodeShell: React.FC<{
  id: string;
  kind: DiagramNodeKind;
  tone: StatusTone;
  icon: React.ReactNode;
  title: string;
  subtitle?: string | null;
  status?: string | null;
  collapsible?: boolean;
  collapsed?: boolean;
  hiddenSummary?: string | null;
  onToggleCollapse?: (nodeId: string) => void;
  showLeftHandle?: boolean;
  showRightHandle?: boolean;
}> = ({
  id,
  kind,
  tone,
  icon,
  title,
  subtitle,
  status,
  collapsible,
  collapsed,
  hiddenSummary,
  onToggleCollapse,
  showLeftHandle = true,
  showRightHandle = true,
}) => {
  const { t } = useTranslation();
  const c = toneClasses(tone);
  const style: React.CSSProperties = {
    width: NODE_WIDTH,
    minHeight: NODE_MIN_HEIGHT,
  };
  return (
    <div
      className={[
        'relative rounded-xl border-2 px-3 py-2 select-none',
        c.bg,
        c.border,
      ].join(' ')}
      style={style}
      data-kind={kind}
    >
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{ ...hiddenHandleStyle, visibility: showLeftHandle ? 'visible' : 'hidden' }}
        isConnectable={false}
      />
      <div className="flex items-start gap-2">
        <div className={['mt-0.5 flex-shrink-0', c.text].join(' ')}>{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground truncate" title={title}>
            {title}
          </div>
          {subtitle ? (
            <div className="text-[11px] text-muted-foreground truncate" title={subtitle}>
              {subtitle}
            </div>
          ) : null}
          {status ? (
            <div className="mt-1 flex items-center gap-1.5">
              <span className={['inline-block h-1.5 w-1.5 rounded-full', c.dot].join(' ')} />
              <span className={['text-[10px] uppercase tracking-wider font-medium', c.text].join(' ')}>
                {status}
              </span>
            </div>
          ) : null}
          {collapsible && collapsed && hiddenSummary ? (
            <div className="mt-1 text-[10px] italic text-muted-foreground truncate" title={hiddenSummary}>
              {hiddenSummary}
            </div>
          ) : null}
        </div>
        {collapsible ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse?.(id);
            }}
            className="flex-shrink-0 mt-0.5 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label={collapsed ? t('forgeDashboard.diagram.expand') : t('forgeDashboard.diagram.collapse')}
          >
            {collapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        ) : null}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{ ...hiddenHandleStyle, visibility: showRightHandle ? 'visible' : 'hidden' }}
        isConnectable={false}
      />
    </div>
  );
};

const UserNodeInner: React.FC<NodeProps<DiagramNodeData>> = ({ id, data }) => (
  <NodeShell
    id={id}
    kind="user"
    tone="muted"
    icon={<UserRound className="h-4 w-4" />}
    title={data.label}
    subtitle={data.sublabel}
    showLeftHandle={false}
  />
);

const OrgNodeInner: React.FC<NodeProps<DiagramNodeData>> = ({ id, data }) => (
  <NodeShell
    id={id}
    kind="org"
    tone="muted"
    icon={<Building2 className="h-4 w-4" />}
    title={data.label}
    subtitle={data.sublabel}
  />
);

const ClientNodeInner: React.FC<NodeProps<DiagramNodeData>> = ({ id, data }) => (
  <NodeShell
    id={id}
    kind="client"
    tone="info"
    icon={<Server className="h-4 w-4" />}
    title={data.label}
    subtitle={data.sublabel}
    collapsible={data.collapsible}
    collapsed={data.collapsed}
    hiddenSummary={data.hiddenSummary}
    onToggleCollapse={data.onToggleCollapse}
  />
);

const ForgeNodeInner: React.FC<NodeProps<DiagramNodeData>> = ({ id, data }) => (
  <NodeShell
    id={id}
    kind="forge"
    tone={toneFromStatus(data.status)}
    icon={<Layers className="h-4 w-4" />}
    title={data.label}
    subtitle={data.sublabel}
    status={data.status}
    collapsible={data.collapsible}
    collapsed={data.collapsed}
    hiddenSummary={data.hiddenSummary}
    onToggleCollapse={data.onToggleCollapse}
  />
);

const AppNodeInner: React.FC<NodeProps<DiagramNodeData>> = ({ id, data }) => (
  <NodeShell
    id={id}
    kind="app"
    tone={toneFromStatus(data.status)}
    icon={<AppWindow className="h-4 w-4" />}
    title={data.label}
    subtitle={data.sublabel}
    status={data.status}
    showRightHandle={false}
  />
);

export const UserNode = memo(UserNodeInner);
export const OrgNode = memo(OrgNodeInner);
export const ClientNode = memo(ClientNodeInner);
export const ForgeNode = memo(ForgeNodeInner);
export const AppNode = memo(AppNodeInner);

export const diagramNodeTypes = {
  user: UserNode,
  org: OrgNode,
  client: ClientNode,
  forge: ForgeNode,
  app: AppNode,
};

export const NODE_DIMENSIONS = { width: NODE_WIDTH, minHeight: NODE_MIN_HEIGHT };
